import { axios, transformAxiosError } from "../utils/axios.create";
import {
  Organization,
  OrganizationPayload,
} from "../domain/organizations.interface";

const findByIds = async ({
  ids,
  status,
}: {
  ids: string[];
  status: string;
}): Promise<Organization[]> => {
  const statusQuery = new URLSearchParams({
    status: status,
    _id: ids.join(","),
  }).toString();

  return axios
    .get(`/organizations?${statusQuery}`)
    .then((res) => {
      return res.data.body as Organization[];
    })
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const findActive = async (): Promise<Organization[]> => {
  const queryParams = new URLSearchParams({ status: "ACTIVE" });
  return axios
    .get(`/organizations?${queryParams.toString()}`)
    .then((res) => (res.data.body || []) as Organization[])
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export type OrganizationsFindAllPayload = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
};

const findAll = async ({
  sortField,
  sortOrder,
  pageNumber,
  pageSize,
  search,
  status,
}: OrganizationsFindAllPayload): Promise<{
  list: Organization[];
  totalCount: number;
}> => {
  const queryParams = new URLSearchParams({
    search,
    status,
    pageNumber: String(pageNumber + 1),
    pageSize: String(pageSize),
    sortField,
    sortOrder: sortOrder === 1 ? "asc" : "desc",
  });

  return axios
    .get(`/organizations/search?${queryParams.toString()}`)
    .then((res) => ({
      list: res.data.body.list as Organization[],
      totalCount: res.data.body.totalCount as number,
    }))
    .catch((err) => {
      return Promise.reject(transformAxiosError(err));
    });
};

const createOne = async (
  payload: OrganizationPayload
): Promise<Organization> => {
  return axios
    .post("/organizations", payload)
    .then((res) => res.data.body as Organization)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const updateOne = async ({
  id,
  payload,
}: {
  id: string;
  payload: OrganizationPayload;
}): Promise<Organization> => {
  return axios
    .put(`/organizations/${id}`, payload)
    .then((res) => res.data.body as Organization)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

const archiveOne = async (id: string): Promise<void> => {
  return axios
    .delete(`/organizations/${id}`)
    .then(() => undefined)
    .catch((err) => Promise.reject(transformAxiosError(err)));
};

export const OrganizationService = {
  findByIds,
  findActive,
  findAll,
  createOne,
  updateOne,
  archiveOne,
};
